import { StringConvertRule, STRING_CONVERT_RULE } from 'shared';

type CharItem = {
  [key: string]: string;
};

type Converter = (value: string) => string;

export const convertString =
  (rule?: StringConvertRule) =>
  (value: string): string => {
    switch (rule) {
      case STRING_CONVERT_RULE.fullToHalf:
        return convertAllFullToHalf(value);
      case STRING_CONVERT_RULE.halfToFull:
        return convertAllHalfToFull(value);
      case STRING_CONVERT_RULE.toFullHiragana:
        return convertToFullHiragana(value);
      case STRING_CONVERT_RULE.toFullKatakana:
        return convertToFullKatakana(value);
      case STRING_CONVERT_RULE.toHalfKatakana:
        return convertToHalfKatakana(value);
      case STRING_CONVERT_RULE.toHalfLower:
        return convertToHalfLower(value);
      default:
        return value;
    }
  };

// Main function
// 全角 → 半角
export const convertAllFullToHalf: Converter = (value) => {
  return convertFullToHalfKatakana(convertFullToHalf(value));
};
// 半角 → 全角
export const convertAllHalfToFull: Converter = (value) => {
  return convertHalfToFullKatakana(convertHalfToFull(value));
};
// 全角カナ・半角カナ → 全角かな
export const convertToFullHiragana: Converter = (value) => {
  return convertFullKatakanaToFullHiragana(convertHalfToFullKatakana(value));
};
// 全角かな・半角カナ → 全角カナ
export const convertToFullKatakana: Converter = (value) => {
  return convertFullHiraganaToFullKatakana(convertHalfToFullKatakana(value));
};
// 全角かな・全角カナ → 半角カナ
export const convertToHalfKatakana: Converter = (value) => {
  return convertFullToHalfKatakana(convertFullHiraganaToFullKatakana(value));
};
// 全角/半角 → 半角小文字
export const convertToHalfLower: Converter = (value) => {
  return convertFullToHalf(value).toLowerCase();
};

// Convert function
export const convertFullToHalf: Converter = (value) => {
  return value
    .replace(/[！-～]/g, (input) =>
      String.fromCharCode(input.charCodeAt(0) - 0xfee0)
    )
    .replace(/”/g, '"')
    .replace(/’/g, "'")
    .replace(/‘/g, '`')
    .replace(/￥/g, '\\')
    .replace(/\u3000/g, ' ')
    .replace(/〜/g, '~');
};

export const convertHalfToFull: Converter = (value) => {
  return (
    value
      .replace(/[!-~]/g, (input) =>
        String.fromCharCode(input.charCodeAt(0) + 0xfee0)
      )
      .replace(/"/g, '”')
      .replace(/'/g, '’')
      .replace(/`/g, '‘')
      // \\は￥に変換されない。なぜなら上記の/[!-~]/gで＼変換されるため。convertFullToHalfと対にしておくため記述だけコメントで残しておく
      //.replace(/\\/g, '￥')
      .replace(/ /g, '　')
      .replace(/~/g, '〜')
  );
};

export const convertHalfToFullKatakana: Converter = (value) => {
  return convertUsingMap(value, KATAKANA_MAP);
};

export const convertFullToHalfKatakana: Converter = (value) => {
  const fullKanaMap = reverseMap(KATAKANA_MAP);
  return convertUsingMap(value, fullKanaMap);
};

export const convertFullHiraganaToFullKatakana: Converter = (value) => {
  return value.replace(/[\u3041-\u3096]/g, (input) => {
    return String.fromCharCode(input.charCodeAt(0) + 0x60);
  });
};

export const convertFullKatakanaToFullHiragana: Converter = (value) => {
  return value.replace(/[\u30a1-\u30f6]/g, (input) => {
    return String.fromCharCode(input.charCodeAt(0) - 0x60);
  });
};

// Utilities
export const reverseMap = (targetMap: CharItem): CharItem => {
  return Object.keys(targetMap).reduceRight((newMap: CharItem, key: string) => {
    newMap[targetMap[key]] = key;
    return newMap;
  }, {});
};

const convertUsingMap = (value: string, map: CharItem) => {
  const reg = new RegExp('(' + Object.keys(map).join('|') + ')', 'g');

  return value.replace(reg, (match) => {
    return map[match];
  });
};

export const KATAKANA_MAP: CharItem = {
  ｶﾞ: 'ガ',
  ｷﾞ: 'ギ',
  ｸﾞ: 'グ',
  ｹﾞ: 'ゲ',
  ｺﾞ: 'ゴ',
  ｻﾞ: 'ザ',
  ｼﾞ: 'ジ',
  ｽﾞ: 'ズ',
  ｾﾞ: 'ゼ',
  ｿﾞ: 'ゾ',
  ﾀﾞ: 'ダ',
  ﾁﾞ: 'ヂ',
  ﾂﾞ: 'ヅ',
  ﾃﾞ: 'デ',
  ﾄﾞ: 'ド',
  ﾊﾞ: 'バ',
  ﾋﾞ: 'ビ',
  ﾌﾞ: 'ブ',
  ﾍﾞ: 'ベ',
  ﾎﾞ: 'ボ',
  ﾊﾟ: 'パ',
  ﾋﾟ: 'ピ',
  ﾌﾟ: 'プ',
  ﾍﾟ: 'ペ',
  ﾎﾟ: 'ポ',
  ｳﾞ: 'ヴ',
  ﾜﾞ: 'ヷ',
  ｦﾞ: 'ヺ',
  ｱ: 'ア',
  ｲ: 'イ',
  ｳ: 'ウ',
  ｴ: 'エ',
  ｵ: 'オ',
  ｶ: 'カ',
  ｷ: 'キ',
  ｸ: 'ク',
  ｹ: 'ケ',
  ｺ: 'コ',
  ｻ: 'サ',
  ｼ: 'シ',
  ｽ: 'ス',
  ｾ: 'セ',
  ｿ: 'ソ',
  ﾀ: 'タ',
  ﾁ: 'チ',
  ﾂ: 'ツ',
  ﾃ: 'テ',
  ﾄ: 'ト',
  ﾅ: 'ナ',
  ﾆ: 'ニ',
  ﾇ: 'ヌ',
  ﾈ: 'ネ',
  ﾉ: 'ノ',
  ﾊ: 'ハ',
  ﾋ: 'ヒ',
  ﾌ: 'フ',
  ﾍ: 'ヘ',
  ﾎ: 'ホ',
  ﾏ: 'マ',
  ﾐ: 'ミ',
  ﾑ: 'ム',
  ﾒ: 'メ',
  ﾓ: 'モ',
  ﾔ: 'ヤ',
  ﾕ: 'ユ',
  ﾖ: 'ヨ',
  ﾗ: 'ラ',
  ﾘ: 'リ',
  ﾙ: 'ル',
  ﾚ: 'レ',
  ﾛ: 'ロ',
  ﾜ: 'ワ',
  ｦ: 'ヲ',
  ﾝ: 'ン',
  ｧ: 'ァ',
  ｨ: 'ィ',
  ｩ: 'ゥ',
  ｪ: 'ェ',
  ｫ: 'ォ',
  ｯ: 'ッ',
  ｬ: 'ャ',
  ｭ: 'ュ',
  ｮ: 'ョ',
};
